import { useAuth } from "@clerk/clerk-react";
import { Box, Typography } from "@mui/material";
import { AlertContext } from "contexts/Alert";
import { OrganizationUserContext } from "contexts/Organization";
import {
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import {
  FollowUpQuestionOption,
  NicheFollowUpQuestion,
  NicheFollowUpResponse,
} from "schemas/dashboard";
import { routes } from "schemas/routes";

import StyledCard from "components/Styled/Card";
import StyledVaPage from "components/Styled/VAPage";
import QuestionChip from "features/Influencer/VA/VASettings/QuestionChip";
import { fetcherAuth } from "utils/api";
import { useTracking } from "utils/useTracking";

interface Props {
  nicheFollowUpQuestions: NicheFollowUpQuestion[];
  nicheFollowUpResponses: NicheFollowUpResponse[];
  setFollowUpNicheResponses: Dispatch<SetStateAction<NicheFollowUpResponse[]>>;
}

const SelfServeNicheFollowUpQuestionPage = ({
  nicheFollowUpQuestions,
  nicheFollowUpResponses,
  setFollowUpNicheResponses,
}: Props) => {
  const { trackVaStep } = useTracking();
  const navigate = useNavigate();
  const { getToken } = useAuth();
  const { setAlert } = useContext(AlertContext);
  const { organizationVaProfile, currentOrg } = useContext(
    OrganizationUserContext,
  );

  const [followUpQuestions, setFollowUpQuestions] = useState<
    NicheFollowUpQuestion[]
  >([]);
  const [selectedFollowupAnswers, setSelectedFollowupAnswers] = useState<{
    [key in number]: FollowUpQuestionOption[];
  }>({});
  const [nicheToQuestionIdMap, setNicheToQuestionIdMap] = useState<{
    [key in number]: number;
  }>({});
  const [loading, setLoading] = useState<boolean>(false);

  const initializeQuestions = () => {
    const questionsToAsk: NicheFollowUpQuestion[] = [];
    const nicheToQuestionIdMap: { [key in number]: number } = {};
    for (const niche of organizationVaProfile!.niches!) {
      const followUpQuestion = nicheFollowUpQuestions?.find(
        (question) =>
          question.triggerBentoCategoryId === niche?.bentoCategoryId,
      );
      if (followUpQuestion) {
        questionsToAsk.push(followUpQuestion);
        nicheToQuestionIdMap[niche.id!] = followUpQuestion.id;
      }
    }
    setFollowUpQuestions(questionsToAsk);
    setNicheToQuestionIdMap(nicheToQuestionIdMap);
  };

  const initializeResponses = () => {
    const prefilledAnswers: {
      [key in number]: FollowUpQuestionOption[];
    } = {};
    for (const response of nicheFollowUpResponses) {
      prefilledAnswers[response.nicheFollowUpQuestionId] =
        response.selectedOptions;
    }

    if (Object.keys(prefilledAnswers).length > 0) {
      setSelectedFollowupAnswers(prefilledAnswers);
    }
  };

  const updateNiches = async () => {
    const allRequiredQuestionsAnswered = followUpQuestions.every(
      (question) =>
        !question.isRequired ||
        question.id.toString() in selectedFollowupAnswers,
    );

    if (!allRequiredQuestionsAnswered) {
      setAlert(
        "Please answer all required questions before proceeding.",
        "warning",
      );
      return;
    }

    setLoading(true);
    const nichesToUpdate: {
      id: number;
      followUpResponses: { [key in number]: FollowUpQuestionOption[] };
    }[] = [];

    for (const key in nicheToQuestionIdMap) {
      const questionId = nicheToQuestionIdMap[key];
      nichesToUpdate.push({
        id: parseInt(key),
        followUpResponses: {
          [questionId]: selectedFollowupAnswers[questionId],
        },
      });
    }
    try {
      const { niches: newNiches } = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/organization-niches/from-onboarding`,
        "PUT",
        {},
        {
          niches: nichesToUpdate,
        },
      );
      navigate(`/${routes.templatesSelfServeOnboarding}`);
      trackVaStep(routes.templatesSelfServeOnboarding);

      const responses = [];
      if (newNiches?.length > 0) {
        for (const n of newNiches) {
          for (const r of n.followUpResponses) {
            if (r.nicheFollowUpQuestionId) {
              responses.push(r);
            }
          }
        }
      }

      setFollowUpNicheResponses(responses);
    } catch (error) {
      setAlert(
        error?.message ||
          "Unable to save your information. Please reload and try again",
        "error",
      );
      navigate(`/${routes.nicheSelfServeOnboarding}`);
    } finally {
      setLoading(false);
    }
  };

  const nicheIds = organizationVaProfile?.niches?.map((niche) => niche.id);
  const stringifyId = (nicheIds || []).sort().join(",");
  useEffect(() => {
    if (
      organizationVaProfile?.niches &&
      organizationVaProfile?.niches?.length > 0 &&
      nicheFollowUpQuestions &&
      nicheFollowUpQuestions?.length > 0
    ) {
      initializeQuestions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stringifyId, nicheFollowUpQuestions?.length]);

  useEffect(() => {
    if (nicheFollowUpResponses?.length > 0) {
      initializeResponses();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nicheFollowUpResponses?.length]);

  const optionIsSelected = (
    followUpQuestion: NicheFollowUpQuestion,
    option: FollowUpQuestionOption,
  ) => {
    return selectedFollowupAnswers[followUpQuestion.id]?.some(
      (selectedOption) => selectedOption.option === option.option,
    );
  };

  return (
    <StyledVaPage
      stepNumber={2}
      annotation="(cont.)"
      title="Additional Questions on Your Niches 🔍"
      description="Help us understand your needs better by answering these questions."
      body={
        <StyledCard styles={{ px: 2, py: 4, mb: 4 }}>
          {followUpQuestions?.map((followUpQuestion) => (
            <Box key={followUpQuestion.id} sx={{ mt: 2 }}>
              <Box>
                <Typography variant="h6">
                  {followUpQuestion.question}{" "}
                  {followUpQuestion.isRequired ? "*" : "(optional)"}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="textSecondary" paragraph>
                  {followUpQuestion.description}
                </Typography>
              </Box>
              <Box display="flex" gap={1} sx={{ mb: 2 }} flexWrap="wrap">
                {followUpQuestion.options.map(
                  (option: FollowUpQuestionOption) => {
                    const isSelected = optionIsSelected(
                      followUpQuestion,
                      option,
                    );
                    return (
                      <QuestionChip
                        isSelected={isSelected}
                        onClick={() => {
                          if (isSelected) {
                            setSelectedFollowupAnswers((prev) => {
                              const copy = { ...prev };
                              copy[followUpQuestion.id] = copy[
                                followUpQuestion.id
                              ].filter((v) => v.option !== option.option);
                              return copy;
                            });
                          } else {
                            setSelectedFollowupAnswers((prev) => {
                              const copy = { ...prev };
                              copy[followUpQuestion.id] = [
                                ...(copy[followUpQuestion.id] || []),
                                option,
                              ];
                              return copy;
                            });
                          }
                        }}
                        key={option.option}
                        label={option.option}
                      />
                    );
                  },
                )}
              </Box>
            </Box>
          ))}
        </StyledCard>
      }
      buttonBack={"Go back to Niches"}
      onBack={() => {
        trackVaStep(routes.nicheSelfServeOnboarding);
        navigate(`/${routes.nicheSelfServeOnboarding}`);
      }}
      buttonNext="Continue To Templates"
      onNext={updateNiches}
      loading={loading}
    />
  );
};

export default SelfServeNicheFollowUpQuestionPage;
